/* global globalThis */
/**
 * Exporte un flux HLS chiffré (AES-128-CBC) en un seul fichier .ts lisible
 * (VLC, ffmpeg), déchiffré dans le navigateur avec la clé média : utilisé
 * quand le serveur ne détient pas la clé (v2).
 *
 * IV : attribut IV de #EXT-X-KEY s'il existe, sinon numéro du segment
 * (celui du nom segment_NNN, comme le muxer ffmpeg), sinon rang dans la playlist.
 */
export function ivFor(sequence) {
    // Numéro de segment sur 128 bits big-endian (les 4 derniers octets suffisent).
    const iv = new Uint8Array(16);
    const value = Math.max(0, Math.floor(sequence)) >>> 0;
    iv[12] = (value >>> 24) & 0xff;
    iv[13] = (value >>> 16) & 0xff;
    iv[14] = (value >>> 8) & 0xff;
    iv[15] = value & 0xff;
    return iv;
}

export function parsePlaylist(text, baseUrl) {
    const lines = text.split(/\r?\n/).map(line => line.trim());
    let mediaSequence = 0;
    let explicitIv = null;
    const segments = [];
    for (const line of lines) {
        if (line.startsWith('#EXT-X-MEDIA-SEQUENCE:')) mediaSequence = Number(line.split(':')[1]) || 0;
        else if (line.startsWith('#EXT-X-KEY:')) {
            const match = line.match(/IV=0x([0-9a-fA-F]{32})/);
            explicitIv = match ? Uint8Array.from(match[1].match(/../g).map(byte => parseInt(byte, 16))) : null;
        } else if (line && !line.startsWith('#')) {
            const url = new URL(line, baseUrl).toString();
            const named = line.match(/segment_(\d+)/);
            const sequence = named ? Number(named[1]) : mediaSequence + segments.length;
            segments.push({ url, iv: explicitIv || ivFor(sequence) });
        }
    }
    return segments;
}

export async function downloadDecryptedHls({ playlistUrl, headers = {}, key, onProgress = () => {} }) {
    const playlistResponse = await fetch(playlistUrl, { headers });
    if (!playlistResponse.ok) throw new Error(`Playlist indisponible (${playlistResponse.status})`);
    const segments = parsePlaylist(await playlistResponse.text(), playlistUrl);
    if (segments.length === 0) throw new Error('Aucun segment.');
    const cryptoKey = await globalThis.crypto.subtle.importKey('raw', key, 'AES-CBC', false, ['decrypt']);
    const parts = [];
    for (const [index, segment] of segments.entries()) {
        onProgress(`segment ${index + 1}/${segments.length}`);
        const response = await fetch(segment.url, { headers });
        if (!response.ok) throw new Error(`Segment indisponible (${response.status})`);
        const clear = await globalThis.crypto.subtle.decrypt({ name: 'AES-CBC', iv: segment.iv }, cryptoKey, await response.arrayBuffer());
        parts.push(new Uint8Array(clear));
    }
    return new Blob(parts, { type: 'video/mp2t' });
}
